<template>
  <div id="generate-token-page">
    <page-heading page-title="Token Generation"
                  v-bind:links="[
                      {target: '#help-modal', text: 'Help'},
                      {target: '/', text: 'Login'}
                    ]">
    </page-heading>
    <div id="content" class="container">
      <div class="row">
        <div class="col">
          <page-alert v-bind:class="{'invisible': isBridgeConnected}">
            <div slot="message">Error connecting to the Real-time Interface</div>
          </page-alert>
        </div>
      </div>
      <div class="row">
        <div id="token-info-input" class="col-8 mr-auto ml-auto form-group">
          <form id="generate-token-form" class="form-group" novalidate autocomplete="off"
                @submit.prevent="generateToken()">
            <div id="user-id" class="form-group row">
              <label for="user-id-input" class="col-2 col-form-label">User ID</label>
              <div class="col">
                <input type="text" id="user-id-input" class="form-control" placeholder="User ID" v-model="userId.value"
                       required>
                <div class="invalid-feedback">Enter a user ID</div>
              </div>
            </div>
            <div id="telescope" class="form-group row">
              <label for="telescope-input" class="col-2 col-form-label">Telescope</label>
              <div class="col">
                <select id="telescope-input" v-model="telescope.value" class="form-control" required>
                  <option value="">Select a telescope...</option>
                  <option v-for="telescope in availableTelescopes" :value="telescope">{{ telescope.name }}</option>
                </select>
                <div class="invalid-feedback">Select a telescope</div>
              </div>
            </div>
            <div id="date-range" class="form-group row">
              <label for="date-range-input" class="col-2 col-form-label">Date</label>
              <div class="col">
                <input id="date-range-input" placeholder="Select a date..."
                       class="form-control flatpickr-readonly" v-model="dateRange.value" required>
                <div class="invalid-feedback">Select a date</div>
              </div>
            </div>
            <div id="start-time" class="form-group row">
              <label for="start-time-input" class="col-2 col-form-label">Time</label>
              <div class="col">
                <div class="input-group">
                  <input id="start-time-input" placeholder="Select a start time..."
                         class="form-control flatpickr-readonly" v-model="startTime.value" required>
                  <span class="input-group-addon">UTC</span>
                </div>
                <div class="invalid-feedback">Select a start time</div>
              </div>
              <div class="col">
                <div class="input-group">
                  <input id="end-timestamp-input" placeholder="Select an end time..."
                         class="form-control flatpickr-readonly" v-model="endTime.value" required>
                  <span class="input-group-addon">UTC</span>
                </div>
                <div class="invalid-feedback">Select an end time</div>
              </div>
            </div>
            <div id="password" class="form-group row">
              <label for="password-input" class="col-2 col-form-label">Password</label>
              <div class="col">
                <input id="password-input" type="password" placeholder="Password" class="form-control"
                       v-model="password.value" required>
                <div class="invalid-feedback">{{ password.feedback }}</div>
              </div>
            </div>
            <div id="generated-token" class="form-group row">
              <input type="text" id="token-string" class="form-control col" v-model="token.value"
                     placeholder="Generated token" readonly/>
              <div class="invalid-feedback">{{ token.feedback }}</div>
              <span class="input-group-btn">
              <button id="copy-token" class="btn btn-secondary" type="button" data-clipboard-target="#token-string"
                      data-toggle="popover" @click="copiedPopover()" disabled>
              <img src="../img/ic_content_copy_black_24px.svg" alt="Copy to clipboard">
              </button>
            </span>
            </div>
            <div class="row pb-3">
              <div class="col" style="color: #dc3545; font-size: .875rem;">{{ token.feedback }}</div>
            </div>
            <div id="generate-button" class="row pb-5">
              <div class="col d-flex justify-content-center">
                <button class="btn btn-primary" type="submit" :disabled="!isBridgeConnected">
                  Generate
                </button>
              </div>
            </div>
          </form>
        </div>
        <help-modal>
          <div slot="help-text">
            <p>
              Allows an authorized user or application to generate login tokens for the Real-time Interface (RTI). All
              of the fields on this page must be supplied. The generated token will be valid to login to the RTI
              during the session as well as 5 minutes before the session start and 15 minutes after the session end.
              Please copy this token and store it for later use with the RTI.
            </p>
            <p>
              The password is provided by the Observing Manager and is not part of any wider LCO user management.
            </p>
          </div>
        </help-modal>
      </div>
    </div>
  </div>
</template>

<script>
  import $ from 'jquery';
  import Clipboard from 'clipboard';
  import Flatpickr from 'flatpickr';
  import Moment from 'moment';
  import _ from 'lodash';
  import { bridgeConnection, initValidation } from '../js/common';
  import PageAlert from 'pageAlert.vue';
  import PageHeading from 'pageHeading.vue';
  import HelpModal from 'helpModal.vue';

  import 'flatpickr/dist/flatpickr.css';

  export default {
    name: 'app',
    components: {
      PageAlert,
      PageHeading,
      HelpModal
    },
    data: function() {
      return {
        userId: {
          value: '',
          feedback: ''
        },
        telescope: {
          value: '',
          feedback: ''
        },
        availableTelescopes: [],
        dateRange: {
          value: '',
          feedback: ''
        },
        startTime: {
          value: '',
          feedback: ''
        },
        endTime: {
          value: '',
          feedback: ''
        },
        password: {
          value: '',
          feedback: ''
        },
        token: {
          value: '',
          feedback: ''
        },
        tokenGenUrl: '',
        tokenGenPath: '/generate-token',
        isBridgeConnected: true,
        popoverDefaults: {
          placement: 'top',
          trigger: 'manual'
        },
        timer: null,
        errorMessage: ''
      };
    },
    mounted: function() {
      this.tokenGenUrl = bridgeConnection.bridgeUrl + this.tokenGenPath;

      this.getAvailableTelescopes();

      this.initCalendar();
      this.initClipboard();
      initValidation('generate-token-form');

      this.timer = setInterval(
        this.tick, 4000
      );
    },
    beforeDestroy: function() {
      clearInterval(this.timer);
      $('.popover').popover('dispose');
    },
    methods: {
      initCalendar: function() {
        /* Careful, Flatpickr doesn't return Unix time as per their documentation. Handled it manually in Vue. */
        const flatpickrDateRangeConfig = {
          dateFormat: 'Y-m-d',
          minDate: Moment.utc().format()
        };

        const flatpickrTimeConfig = {
          enableTime: true,
          noCalendar: true,
          time_24hr: true,
          minuteIncrement: 1,
          dateFormat: 'H:i'
        };

        Flatpickr(document.getElementById('date-range-input'), flatpickrDateRangeConfig);
        Flatpickr(document.getElementById('start-time-input'), flatpickrTimeConfig);
        Flatpickr(document.getElementById('end-timestamp-input'), flatpickrTimeConfig);
      },
      initClipboard: function() {
        new Clipboard('#copy-token').on('success', function(e) {
          e.clearSelection();
        });
      },
      generateToken: function() {
        this.token.value = '';

        if (this.isInputEmpty())
          return;

        this.checkPassword();
      },
      isInputEmpty: function() {
        let isEmpty = false;

        for (let element of document.getElementById('generate-token-form').querySelectorAll('input')) {
          if (element.id !== 'token-string') {
            if (element.checkValidity())
              element.setCustomValidity('');

            isEmpty &= !element.checkValidity();
          }
        }

        return isEmpty;
      },
      checkPassword: function() {
        let xhr = new XMLHttpRequest();
        xhr.open('POST', this.tokenGenUrl, true);
        xhr.setRequestHeader('Content-type', 'application/json');

        xhr.onerror = () => {
          this.password.feedback = 'Error while authenticating password';
        };

        xhr.onload = () => {
          let jsonResponse = JSON.parse(xhr.responseText);

          if (jsonResponse.description) {
            let description = jsonResponse.description.toLowerCase();
            this.handleBridgeError(description);
          } else if (jsonResponse.token) {
            this.token.value = jsonResponse.token;
            this.token.feedback = '';

            let options = {};
            options.placement = this.popoverDefaults.placement;
            options.trigger = this.popoverDefaults.trigger;
            options.title = 'Click to copy to clipboard';
            options.content = 'Store this token for later use accessing the RTI';

            $('#copy-token').popover('dispose').popover(options).popover('show');

            document.getElementById('copy-token').disabled = false;
            document.getElementById('password-input').setCustomValidity('');
          } else {
            console.error('Error handling JSON response');
          }
        };

        xhr.send(this.rtiSessionJson);
      },
      handleBridgeError: function(description) {
        if (description === 'invalid password') {
          this.password.feedback = 'Invalid password';
          document.getElementById('password-input').setCustomValidity(this.password.feedback);
        } else if (description === 'window not valid') {
          this.token.feedback = 'This observation window is not available for the selected telescope';
          this.startTime.feedback = ' ';
          this.endTime.feedback = ' ';
          this.dateRange.feedback = ' ';
          this.telescope.feedback = ' ';
          document.getElementById('password-input').setCustomValidity('');
        } else if (description === 'invalid session length') {
          this.token.feedback = 'The session length is invalid';
          this.startTime.feedback = ' ';
          this.endTime.feedback = ' ';
          document.getElementById('password-input').setCustomValidity('');
        }
      },
      getAvailableTelescopes: function() {
        let xhr = new XMLHttpRequest();

        xhr.onerror = () => {
          this.telescope.feedback = 'Cannot retrieve available telescopes';
        };

        xhr.open('GET', bridgeConnection.bridgeUrl + '/available-telescopes', true);
        xhr.setRequestHeader('Content-type', 'application/json');
        xhr.onload = () => {
          this.availableTelescopes = _.orderBy(JSON.parse(xhr.responseText), ['name']);
        };

        xhr.send();
      },
      copiedPopover: function() {
        let options = {};
        options.placement = this.popoverDefaults.placement;
        options.trigger = this.popoverDefaults.trigger;
        options.content = 'Token copied to clipboard';

        $('#copy-token').popover('dispose').popover(options).popover('show');
      },
      tick: function() {
        bridgeConnection.isConnected((results) => {
          this.isBridgeConnected = results;
        });
      }
    },
    computed: {
      rtiSessionJson: function() {
        let rtiSessionInfo = {
          'userId': this.userId.value,
          'telescopeInfo': this.telescope.value,
          'activeSessionStart': this.activeSessionStart,
          'activeSessionEnd': this.activeSessionEnd,
          'password': this.password.value
        };

        return JSON.stringify(rtiSessionInfo);
      },
      /* This is ready to use a start and end date when it will be needed */
      activeSessionStart: function() {
        let startDate = this.dateRange.value;

        return Moment.utc(startDate + 'T' + this.startTime.value).unix();
      },
      activeSessionEnd: function() {
        let endDate = this.dateRange.value;

        return Moment.utc(endDate + 'T' + this.endTime.value).unix();
      }
    }
  };
</script>
